import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import StoryReferenceWithGrid from "../../../admin/story/StoryReferenceWithGrid";
import {
    screenshotSceneKey,
    useScreenshotTranslation,
} from "../../../lib/screenshotI18n";
import { screenshotFontStack, screenshotShellFontSx } from "../../../lib/screenshotFonts";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import ScreenshotFloatingPieces from "../components/ScreenshotFloatingPieces";
import ScreenshotGiantExpertBoard, {
    ScreenshotGiantGlassTiles,
} from "../components/ScreenshotGiantExpertBoard";
import ScreenshotNumberBoard from "../components/ScreenshotNumberBoard";
import ScreenshotGhostGuidePanel, {
    ScreenshotGhostGuideOverlay,
} from "../components/ScreenshotGhostGuidePanel";
import ScreenshotPhoneFrame from "../components/ScreenshotPhoneFrame";
import ScreenshotPlayBoardTray from "../components/ScreenshotPlayBoardTray";
import ScreenshotPlayToolbar from "../components/ScreenshotPlayToolbar";
import ScreenshotScrambledBoard from "../components/ScreenshotScrambledBoard";
import ScreenshotPromoHeader from "../components/ScreenshotPromoHeader";
import ScreenshotSizeDifficultyBar from "../components/ScreenshotSizeDifficultyBar";
import ScreenshotTagBar from "../components/ScreenshotTagBar";
import ScreenshotTwoColumnGallery from "../components/ScreenshotTwoColumnGallery";
import ScreenshotHomeScene from "../components/ScreenshotHomeScene";
import ScreenshotPuzzlesScene from "../components/ScreenshotPuzzlesScene";
import SlidingShowcaseGoogleAdCreative from "../components/SlidingShowcaseGoogleAdLayouts";
import ScreenshotDesignFrame from "../../../components/ScreenshotDesignFrame";
import { resolveScreenshotDevice } from "../../../components/ScreenshotDevicePicker";
import {
    buildGhostGuideMovesFast,
    GHOST_HINT_MOVE_COUNT,
} from "../../../pages/puzzles/sliding/slidingSolveHint";
import {
    getImageUrl,
    getStoryTitle,
    getTemplateDifficultyLevel,
    getTemplateGrid,
    loadScreenshotGallery,
    loadScreenshotSliding,
} from "../lib/screenshotApi";
import { isScreenshotSceneExpired } from "../lib/screenshotAccess";
import { SCREENSHOTS_MAIN_PATH } from "../routes";
import {
    getScreenshotScene,
    isScreenshotSceneConfigured,
    isTwoColumnGalleryScene,
} from "../screenshotScenes";
import {
    boardFrameSx,
    boardRegionFramedSx,
    boardRegionSx,
    classicShellSx,
    galleryGridRegionFramedSx,
    galleryPhoneShellSx,
    ghostGuideBoardWrapSx,
    ghostGuideShellSx,
    giantShellSx,
    mexicoShellSx,
    phoneShellSx,
    promoSubtitleSx,
    puzzleTitleBarSx,
    slidingScreenshotAccent,
    slidingShellBgForScene,
    themesShellSx,
} from "../styles/screenshotStyles";

function resolveShellSx(marketing, theme) {
    if (theme === "light") return phoneShellSx;
    if (marketing === "classic") return classicShellSx;
    if (marketing === "giant") return giantShellSx;
    if (marketing === "mexico") return mexicoShellSx;
    if (marketing === "themes") return themesShellSx;
    if (marketing === "ghostGuide") return ghostGuideShellSx;
    return phoneShellSx;
}

const CAPTURE_FILL_SX = {
    width: "100%",
    height: "100%",
    minHeight: "100%",
    maxHeight: "100%",
    boxSizing: "border-box",
};

function sceneCaptureBackground(scene) {
    if (scene?.layout === "home" || scene?.layout === "puzzles") {
        return "#f2f2f7";
    }
    return slidingShellBgForScene(scene);
}

/** Compose at 430×940 on iPhone 6.7" / iPad 13" so title + toolbar stay in frame. */
function SlidingCaptureFrame({ scene, sx, children }) {
    return (
        <ScreenshotDesignFrame background={sceneCaptureBackground(scene)}>
            <Box sx={{ ...sx, ...CAPTURE_FILL_SX }}>{children}</Box>
        </ScreenshotDesignFrame>
    );
}

/**
 * App Store screenshot play scene — promo copy on top, full-bleed board or two-column gallery.
 */
export default function ScreenshotScenePage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const pcLandscape = resolveScreenshotDevice(searchParams.get("device")) === "pc";
    const isSquareAd = searchParams.get("googleAdSquare") === "1";
    const isLandscapeAd = searchParams.get("googleAdLandscape") === "1";
    const facebookAdImageOnly = searchParams.get("facebookAdImageOnly") === "1";
    const isShowcaseAd = isSquareAd || isLandscapeAd;
    const { t, lang } = useScreenshotTranslation();
    const scene = getScreenshotScene(slug);
    const isHome = scene?.layout === "home";
    const isPuzzles = scene?.layout === "puzzles";
    const isGallery = isTwoColumnGalleryScene(scene);
    const loadGallery = isGallery || isPuzzles;
    const marketing = scene?.marketing || null;
    const framed = marketing === "classic" || marketing === "giant";
    const isMexico = marketing === "mexico";
    const isGhostGuide = marketing === "ghostGuide";
    const isGiant = marketing === "giant";
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [galleryItems, setGalleryItems] = useState([]);

    useEffect(() => {
        if (isScreenshotSceneExpired(slug)) {
            setLoading(false);
            return undefined;
        }

        if (!scene || !isScreenshotSceneConfigured(scene)) {
            setError(scene ? "Screenshot scene is not configured yet" : "Unknown screenshot scene");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setGalleryItems([]);
            setGroup(null);
            setTemplate(null);
            try {
                if (loadGallery) {
                    const items = await loadScreenshotGallery(scene);
                    if (cancelled) return;
                    if (!items.length) {
                        setError("No puzzles found for this gallery scene");
                        return;
                    }
                    setGalleryItems(items);
                    setError(null);
                    return;
                }

                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
                const grid = getTemplateGrid(result.template);
                if (
                    scene.expectedGrid &&
                    !scene.slidingId &&
                    grid !== scene.expectedGrid
                ) {
                    setError(
                        `Expected ${scene.expectedGrid}×${scene.expectedGrid} puzzle but loaded ${grid}×${grid}.`,
                    );
                } else {
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message ?? "Failed to load sliding puzzle");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scene, slug, loadGallery]);

    const grid = useMemo(() => (template ? getTemplateGrid(template) : 0), [template]);
    const imageUrl = getImageUrl(group?.story);
    const title = getStoryTitle(group?.story);
    const puzzleTags = useMemo(() => {
        const fromTemplate = group?.puzzleTemplate?.tags ?? [];
        if (fromTemplate.length > 0) return fromTemplate;
        return scene?.label ? [scene.label] : [];
    }, [group, scene?.label]);
    const startingLayout = template?.startingLayout ?? null;

    const ghostGuide = useMemo(() => {
        if (!isGhostGuide || !Array.isArray(startingLayout) || grid <= 0) return null;
        try {
            const moves = buildGhostGuideMovesFast(
                startingLayout,
                grid,
                null,
                Math.min(4, GHOST_HINT_MOVE_COUNT),
            );
            return moves.length ? { mode: "all", moves, stepIndex: null } : null;
        } catch {
            return null;
        }
    }, [isGhostGuide, startingLayout, grid]);

    const galleryImageUrls = useMemo(
        () => galleryItems.map((item) => getImageUrl(item?.group?.story)).filter(Boolean),
        [galleryItems],
    );

    useEffect(() => {
        markScreenshotNotReady();
    }, [slug, lang]);

    useEffect(() => {
        if (loading || error) return undefined;

        if (loadGallery) {
            if (!galleryItems.length || !galleryImageUrls.length) return undefined;
            let cancelled = false;
            (async () => {
                await signalScreenshotSceneReady({
                    imageUrls: galleryImageUrls,
                    fontFamily: screenshotFontStack(lang),
                    lang,
                });
                if (cancelled) markScreenshotNotReady();
            })();
            return () => {
                cancelled = true;
            };
        }

        const layoutReady = Array.isArray(startingLayout) || scene.layout === "playBoard";
        if (!template || !imageUrl || grid <= 0 || !layoutReady) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            await signalScreenshotSceneReady({
                imageUrl,
                fontFamily: screenshotFontStack(lang),
                lang,
            });
            if (cancelled) markScreenshotNotReady();
        })();
        return () => {
            cancelled = true;
        };
    }, [
        loading,
        error,
        loadGallery,
        galleryItems,
        galleryImageUrls,
        template,
        imageUrl,
        grid,
        startingLayout,
        lang,
        slug,
        scene,
    ]);

    if (isScreenshotSceneExpired(slug)) {
        return <ScreenshotExpiredPage />;
    }

    if (!scene) {
        return (
            <SlidingCaptureFrame scene={scene} sx={phoneShellSx}>
                <Box sx={{ p: 2 }}>
                    <Typography color="error">{t("mobileScreenshot.common.scene-not-found")}</Typography>
                    <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                        {t("mobileScreenshot.common.back-to-index")}
                    </Link>
                </Box>
            </SlidingCaptureFrame>
        );
    }

    if (!isScreenshotSceneConfigured(scene)) {
        return (
            <SlidingCaptureFrame scene={scene} sx={phoneShellSx}>
                <Box sx={{ p: 2 }}>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                        Screenshot {scene.slug} — add a puzzle in `screenshotScenes.js`.
                    </Typography>
                    <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                        {t("mobileScreenshot.common.back-to-index")}
                    </Link>
                </Box>
            </SlidingCaptureFrame>
        );
    }

    if (isShowcaseAd) {
        return (
            <Box sx={screenshotShellFontSx(lang)}>
                <SlidingShowcaseGoogleAdCreative
                    scene={scene}
                    lang={lang}
                    variant={isSquareAd ? "square" : "landscape"}
                />
            </Box>
        );
    }

    if (loading) {
        return (
            <SlidingCaptureFrame
                scene={scene}
                sx={{ ...phoneShellSx, alignItems: "center", justifyContent: "center" }}
            >
                <CircularProgress size={32} sx={{ color: slidingScreenshotAccent }} />
            </SlidingCaptureFrame>
        );
    }

    const appShellSx = {
        ...screenshotShellFontSx(lang),
        p: 0,
        overflow: "hidden",
        background: "#f2f2f7",
    };

    if (isPuzzles) {
        if (error || !galleryItems.length) {
            return (
                <SlidingCaptureFrame scene={scene} sx={appShellSx}>
                    <Box sx={{ p: 2 }}>
                        <Typography color="error" sx={{ mb: 1 }}>
                            {error ?? t("mobileScreenshot.common.could-not-load")}
                        </Typography>
                        <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                            {t("mobileScreenshot.common.back-to-index")}
                        </Link>
                    </Box>
                </SlidingCaptureFrame>
            );
        }
        return (
            <SlidingCaptureFrame scene={scene} sx={appShellSx}>
                <ScreenshotPuzzlesScene items={galleryItems} />
            </SlidingCaptureFrame>
        );
    }

    if (isGallery) {
        if (error || !galleryItems.length) {
            return (
                <SlidingCaptureFrame scene={scene} sx={phoneShellSx}>
                    <Box sx={{ p: 2 }}>
                        <Typography color="error" sx={{ mb: 1 }}>
                            {error ?? t("mobileScreenshot.common.could-not-load")}
                        </Typography>
                        <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                            {t("mobileScreenshot.common.back-to-index")}
                        </Link>
                    </Box>
                </SlidingCaptureFrame>
            );
        }

        const themesMode = marketing === "themes";
        const shell = themesMode
            ? { ...themesShellSx, ...screenshotShellFontSx(lang) }
            : { ...galleryPhoneShellSx, ...screenshotShellFontSx(lang) };

        const galleryHeader = facebookAdImageOnly ? null : (
            <ScreenshotPromoHeader
                variant={themesMode ? "themes" : "gallery"}
                promoHeadline={
                    themesMode
                        ? t(screenshotSceneKey("sliding", slug, "headline"))
                        : t("mobileScreenshot.sliding.gallery-title")
                }
                promoSubtitle={
                    themesMode ? t(screenshotSceneKey("sliding", slug, "subtitle")) : undefined
                }
            />
        );
        const galleryGrid = (
            <ScreenshotTwoColumnGallery
                items={galleryItems}
                topCount={scene.galleryTopCount ?? 0}
                columns={scene.galleryColumns ?? 2}
                square={Boolean(scene.gallerySquare)}
                sx={themesMode ? galleryGridRegionFramedSx : undefined}
            />
        );

        return (
            <SlidingCaptureFrame
                scene={scene}
                sx={{
                    ...shell,
                    ...(pcLandscape
                        ? { minHeight: 0, height: "100%", pt: "24px", pb: "20px", px: 3 }
                        : {}),
                }}
            >
                {themesMode ? <ScreenshotFloatingPieces imageUrls={galleryImageUrls} /> : null}
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: pcLandscape ? "row" : "column",
                        alignItems: pcLandscape ? "stretch" : undefined,
                        gap: pcLandscape ? 3 : 0,
                        flex: 1,
                        minHeight: 0,
                        height: pcLandscape ? "100%" : undefined,
                    }}
                >
                    {pcLandscape ? (
                        <Box
                            sx={{
                                flex: "0 1 34%",
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}
                        >
                            {galleryHeader}
                        </Box>
                    ) : (
                        galleryHeader
                    )}
                    <Box sx={pcLandscape ? { flex: "1 1 auto", minWidth: 0, minHeight: 0 } : { display: "contents" }}>
                        {galleryGrid}
                    </Box>
                </Box>
            </SlidingCaptureFrame>
        );
    }

    const layoutReady = Array.isArray(startingLayout) || scene.layout === "playBoard";
    if (error || !template || !imageUrl || grid <= 0 || !layoutReady) {
        return (
            <SlidingCaptureFrame scene={scene} sx={phoneShellSx}>
                <Box sx={{ p: 2 }}>
                    <Typography color="error" sx={{ mb: 1 }}>
                        {error ?? t("mobileScreenshot.common.could-not-load")}
                    </Typography>
                    <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                        {t("mobileScreenshot.common.back-to-index")}
                    </Link>
                </Box>
        </SlidingCaptureFrame>
    );
    }

    if (isHome) {
        return (
            <SlidingCaptureFrame scene={scene} sx={appShellSx}>
                <ScreenshotHomeScene
                    imageUrl={imageUrl}
                    gridSize={grid}
                    startingLayout={startingLayout}
                    title={title}
                />
            </SlidingCaptureFrame>
        );
    }

    const shellSx = resolveShellSx(marketing, scene?.theme);
    const hideStoryMeta = Boolean(scene.hideStoryMeta);
    const hideSizeBar = Boolean(scene.hideSizeBar) || isMexico;
    const showReferenceTray = Boolean(scene.showReferenceTray);
    const onDark =
        scene?.theme !== "light" &&
        (marketing === "classic" || marketing === "giant" || marketing === "mexico");
    const sceneLabel = t(screenshotSceneKey("sliding", slug, "label"));
    const playToolbar = <ScreenshotPlayToolbar onDark={onDark} />;

    const showCornerHole = (slug === "01" || slug === "02" || slug === "08") && !isMexico;
    const scrambledBoard = (
        <ScreenshotScrambledBoard
            imageUrl={imageUrl}
            gridSize={grid}
            startingLayout={startingLayout}
        />
    );
    const board = scene.tileMode === "numbers" ? (
        <ScreenshotNumberBoard gridSize={grid} startingLayout={startingLayout} />
    ) : isGiant ? (
        <ScreenshotGiantExpertBoard
            imageUrl={imageUrl}
            gridSize={grid}
            startingLayout={startingLayout}
        />
    ) : scene.layout === "playBoard" && showReferenceTray ? (
        <ScreenshotPlayBoardTray
            title={title}
            imageUrl={imageUrl}
            grid={grid}
            startingLayout={startingLayout}
            theme={scene?.theme === "light" ? "light" : "dark"}
            hideChrome
        />
    ) : scene.layout === "playBoard" ? (
        scrambledBoard
    ) : (
        <StoryReferenceWithGrid
            imageUrl={imageUrl}
            gridSize={grid}
            alt=""
            squareFrame
            fillSquareParent={!isMexico}
            fillParent={isMexico}
            showEmptySlot={showCornerHole}
            punchEmptySlot={showCornerHole}
            emptySlotBackground={showCornerHole ? "rgba(0, 0, 0, 0.55)" : undefined}
            showGridLines={Boolean(scene.showGridLines)}
        />
    );

    if (pcLandscape && !isMexico) {
        return (
            <SlidingCaptureFrame
                scene={scene}
                sx={{
                    ...shellSx,
                    ...screenshotShellFontSx(lang),
                    minHeight: 0,
                    height: "100%",
                    pt: "24px",
                    pb: "20px",
                    px: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "stretch",
                        gap: 3,
                        flex: 1,
                        minHeight: 0,
                        height: "100%",
                    }}
                >
                    <Box
                        sx={{
                            flex: "0 1 42%",
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        {facebookAdImageOnly ? null : (
                            <ScreenshotPromoHeader
                                variant={isGiant ? "giant" : "promo"}
                                promoHeadline={t(screenshotSceneKey("sliding", slug, "headline"))}
                                promoSubtitle={t(screenshotSceneKey("sliding", slug, "subtitle"))}
                                showPills={!scene.hidePills}
                                light={onDark}
                            />
                        )}
                        {!isGiant && !hideStoryMeta && (title || puzzleTags.length > 0) ? (
                            <Box sx={puzzleTitleBarSx}>
                                {title ? (
                                    <Typography component="p" sx={promoSubtitleSx}>
                                        {title}
                                    </Typography>
                                ) : null}
                                <ScreenshotTagBar tags={puzzleTags} />
                            </Box>
                        ) : null}
                        {isGhostGuide ? (
                            <ScreenshotGhostGuidePanel slug={slug} label={sceneLabel} />
                        ) : hideSizeBar ? null : (
                            <ScreenshotSizeDifficultyBar
                                gridSize={grid}
                                difficultyLevel={isGiant ? "MEDIUM" : getTemplateDifficultyLevel(template)}
                                onDark={onDark}
                                sizeAsGrid={framed || isGiant}
                            />
                        )}
                    </Box>
                        <Box
                            sx={{
                                flex: "1 1 auto",
                                minWidth: 0,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                minHeight: 0,
                            }}
                        >
                            {showReferenceTray ? null : playToolbar}
                            <Box
                                sx={{
                                    ...(isGhostGuide
                                        ? ghostGuideBoardWrapSx
                                        : showReferenceTray
                                          ? {
                                                flex: "1 1 auto",
                                                minHeight: 0,
                                                width: "100%",
                                                maxWidth: "100%",
                                                alignSelf: "stretch",
                                                display: "flex",
                                            }
                                          : {
                                                ...boardFrameSx,
                                                flex: "1 1 auto",
                                                minHeight: 0,
                                                width: "auto",
                                                maxWidth: "100%",
                                                aspectRatio: "1",
                                                alignSelf: "center",
                                            }),
                                }}
                            >
                            {board}
                            {isGhostGuide ? (
                                <ScreenshotGhostGuideOverlay
                                    callout={t("mobileScreenshot.sliding.ghost-callout")}
                                    gridSize={grid}
                                    moves={ghostGuide?.moves}
                                />
                            ) : null}
                        </Box>
                    </Box>
                </Box>
            </SlidingCaptureFrame>
        );
    }

    return (
        <SlidingCaptureFrame scene={scene} sx={{ ...shellSx, ...screenshotShellFontSx(lang) }}>
            {isMexico ? (
                <>
                    <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
                        <Box
                            component="img"
                            src={imageUrl}
                            alt=""
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                    </Box>
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 1,
                            pointerEvents: "none",
                            background:
                                "linear-gradient(180deg, rgba(2,6,23,0.58) 0%, rgba(2,6,23,0.16) 34%, transparent 52%)",
                        }}
                    />
                </>
            ) : null}
            <Box
                sx={
                    isMexico
                        ? {
                              position: "relative",
                              zIndex: 2,
                              pt: "max(env(safe-area-inset-top, 0px), 12px)",
                          }
                        : undefined
                }
            >
                {facebookAdImageOnly ? null : (
                    <ScreenshotPromoHeader
                        variant={isGiant ? "giant" : "promo"}
                        promoHeadline={t(screenshotSceneKey("sliding", slug, "headline"))}
                        promoSubtitle={t(screenshotSceneKey("sliding", slug, "subtitle"))}
                        showPills={!scene.hidePills}
                        light={onDark}
                    />
                )}
            </Box>
            {isMexico ? (
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        flex: "1 1 auto",
                        minHeight: 0,
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1.5,
                        overflow: "hidden",
                        containerType: "size",
                    }}
                >
                    <Box
                        sx={{
                            width: "min(100cqw, calc(100cqh - 44px))",
                            height: "100%",
                            maxHeight: "100%",
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                        }}
                    >
                        {playToolbar}
                        <Box sx={{ flex: "1 1 auto", minHeight: 0, mt: 0, display: "flex" }}>
                            <ScreenshotPhoneFrame sx={{ width: "100%", height: "100%" }}>
                                <Box sx={{ ...boardFrameSx, height: "100%" }}>
                                    <Box sx={{ width: "100%", height: "100%" }}>
                                        <StoryReferenceWithGrid
                                            imageUrl={imageUrl}
                                            gridSize={grid}
                                            alt=""
                                            squareFrame
                                            fillSquareParent
                                            showEmptySlot={false}
                                            showGridLines
                                        />
                                    </Box>
                                </Box>
                            </ScreenshotPhoneFrame>
                        </Box>
                    </Box>
                </Box>
            ) : null}

            {isMexico ? null : (
            <Box
                sx={{
                    ...(framed
                        ? boardRegionFramedSx
                        : showReferenceTray
                          ? {
                                ...boardRegionSx,
                                flex: "1 1 auto",
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                                px: 1.5,
                                pb: 1,
                            }
                          : boardRegionSx),
                    ...(isGiant ? { overflow: "visible" } : {}),
                    ...(slug === "01"
                        ? {
                              alignItems: "stretch",
                              justifyContent: "flex-start",
                              width: "100%",
                          }
                        : {}),
                }}
            >
                {framed ? (
                    slug === "01" ? (
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                minHeight: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                containerType: "size",
                            }}
                        >
                            <Box
                                sx={{
                                    width: "min(100cqw, 100cqh)",
                                    maxHeight: "100%",
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {playToolbar}
                                <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex" }}>
                                    <ScreenshotPhoneFrame
                                        sx={{
                                            position: "relative",
                                            zIndex: 1,
                                            width: "100%",
                                            height: "100%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                ...boardFrameSx,
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Box sx={{ width: "100%", maxHeight: "100%", aspectRatio: "1 / 1" }}>
                                                {board}
                                            </Box>
                                        </Box>
                                    </ScreenshotPhoneFrame>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                    <Box sx={{ position: "relative", width: "100%", maxWidth: "100%" }}>
                        {isGiant && scene.tileMode !== "numbers" ? (
                            <ScreenshotGiantGlassTiles imageUrl={imageUrl} gridSize={grid} />
                        ) : null}
                        <ScreenshotPhoneFrame sx={{ position: "relative", zIndex: 1 }}>
                            <Box sx={boardFrameSx}>
                                {playToolbar}
                                {board}
                            </Box>
                        </ScreenshotPhoneFrame>
                    </Box>
                    )
                ) : isGhostGuide ? (
                    <Box sx={ghostGuideBoardWrapSx}>
                        {playToolbar}
                        {board}
                        <ScreenshotGhostGuideOverlay
                            callout={t("mobileScreenshot.sliding.ghost-callout")}
                            gridSize={grid}
                            moves={ghostGuide?.moves}
                        />
                    </Box>
                ) : showReferenceTray ? (
                    <Box
                        sx={{
                            flex: "1 1 auto",
                            minHeight: 0,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {board}
                    </Box>
                ) : (
                    <Box sx={boardFrameSx}>
                        {playToolbar}
                        {board}
                    </Box>
                )}
            </Box>
            )}

            {!isGiant && !hideStoryMeta && (title || puzzleTags.length > 0) ? (
                <Box sx={puzzleTitleBarSx}>
                    {title ? (
                        <Typography component="p" sx={promoSubtitleSx}>
                            {title}
                        </Typography>
                    ) : null}
                    <ScreenshotTagBar tags={puzzleTags} />
                </Box>
            ) : null}

            {isGhostGuide ? (
                <ScreenshotGhostGuidePanel slug={slug} label={sceneLabel} />
            ) : hideSizeBar ? null : (
                <ScreenshotSizeDifficultyBar
                    gridSize={grid}
                    difficultyLevel={isGiant ? "MEDIUM" : getTemplateDifficultyLevel(template)}
                    onDark={onDark}
                    sizeAsGrid={framed || isGiant}
                />
            )}
        </SlidingCaptureFrame>
    );
}
